"use client"

import { useState, createContext, useContext, useCallback } from "react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { AlertTriangle, Trash2, Info, HelpCircle } from "lucide-react"
import { cn } from "@/lib/utils"

type ConfirmType = "danger" | "warning" | "info" | "question"

interface ConfirmOptions {
  type?: ConfirmType
  title: string
  description: string
  confirmText?: string
  cancelText?: string
}

interface ConfirmContextType {
  confirm: (options: ConfirmOptions) => Promise<boolean>
}

const ConfirmContext = createContext<ConfirmContextType | null>(null)

export function useConfirm() {
  const context = useContext(ConfirmContext)
  if (!context) {
    throw new Error("useConfirm must be used within ConfirmProvider")
  }
  return context
}

const typeStyles: Record<ConfirmType, { icon: typeof AlertTriangle; iconColor: string; buttonClass: string }> = {
  danger: {
    icon: Trash2,
    iconColor: "text-red-600",
    buttonClass: "bg-red-600 hover:bg-red-700 text-white",
  },
  warning: {
    icon: AlertTriangle,
    iconColor: "text-amber-600",
    buttonClass: "bg-amber-600 hover:bg-amber-700 text-white",
  },
  info: {
    icon: Info,
    iconColor: "text-blue-600",
    buttonClass: "bg-blue-600 hover:bg-blue-700 text-white",
  },
  question: {
    icon: HelpCircle,
    iconColor: "text-primary",
    buttonClass: "bg-primary hover:bg-primary/90 text-white",
  },
}

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [options, setOptions] = useState<ConfirmOptions | null>(null)
  const [resolveRef, setResolveRef] = useState<((value: boolean) => void) | null>(null)

  const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setOptions(opts)
      setResolveRef(() => resolve)
      setIsOpen(true)
    })
  }, [])

  const handleConfirm = () => {
    setIsOpen(false)
    resolveRef?.(true)
  }

  const handleCancel = () => {
    setIsOpen(false)
    resolveRef?.(false)
  }

  const type = options?.type || "question"
  const style = typeStyles[type]
  const Icon = style.icon

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      <AlertDialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
        <AlertDialogContent className="sm:max-w-[425px]">
          <AlertDialogHeader>
            <div className="flex items-start gap-4">
              <div className={cn(
                "flex h-12 w-12 shrink-0 items-center justify-center rounded-full",
                type === "danger" && "bg-red-100 dark:bg-red-950",
                type === "warning" && "bg-amber-100 dark:bg-amber-950",
                type === "info" && "bg-blue-100 dark:bg-blue-950",
                type === "question" && "bg-primary/10"
              )}>
                <Icon className={cn("h-6 w-6", style.iconColor)} />
              </div>
              <div className="space-y-2">
                <AlertDialogTitle className="text-lg">
                  {options?.title}
                </AlertDialogTitle>
                <AlertDialogDescription className="text-sm">
                  {options?.description}
                </AlertDialogDescription>
              </div>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel onClick={handleCancel}>
              {options?.cancelText || "Отмена"}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm} className={style.buttonClass}>
              {options?.confirmText || "Подтвердить"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  )
}
















